// Başka firma adına talep: daha önceki firmalardan seçim, benzer ad uyarısı, yeni alt firma oluşturma
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Info } from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { flashElement } from './WizardSection';
import { BehalfMatchPanel, firmLabel } from './BehalfMatchPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const norm = (s) => (s || '').replace(/İ/g, 'i').replace(/I/g, 'ı').toLowerCase()
  .replace(/[çğıöşüâîû]/g, c => ({ ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u', â: 'a', î: 'i', û: 'u' }[c]))
  .replace(/\b(ltd|sti|a\.?s)\b\.?/g, ' ').replace(/[^a-z0-9]+/g, '');
const lev = (a, b) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[a.length][b.length];
};
const similar = (a, b) => a && b && (a.includes(b) || b.includes(a) || lev(a, b) <= Math.max(2, Math.floor(Math.min(a.length, b.length) / 5)));

export const BehalfFirmPicker = ({ name, behalfId, onPick, suffix = ' adına', testPrefix = 'wizard-behalf', placeholder = 'Adına talep oluşturulacak firmanın adı' }) => {
  const [firms, setFirms] = useState([]);
  const [text, setText] = useState(name || '');
  const [suggest, setSuggest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tax, setTax] = useState('');
  const [branch, setBranch] = useState('');
  const [match, setMatch] = useState(null);
  const [different, setDifferent] = useState(false);
  const rootRef = useRef(null);
  const reset = () => { setSuggest(null); setMatch(null); setDifferent(false); };
  const pick = (f) => { reset(); onPick(f && { ...f, name: firmLabel(f) }); };
  useEffect(() => { axios.get(`${API}/work-permits/sub-firms`).then(r => setFirms(r.data)).catch(() => {}); }, []);

  const matches = text.trim().length >= 2 ? firms.filter(f => similar(norm(f.name), norm(text))) : firms;
  const create = async (value) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/work-permits/sub-firms`, { name: value, tax_number: tax.trim() || null, branch: branch.trim() || null, confirm_new: different });
      if (res.data.match) { setSuggest(null); setMatch(res.data.candidates); return; }
      if (!firms.some(f => f.id === res.data.id)) setFirms(prev => [...prev, res.data]);
      if (!res.data.created) toast.info('Bu bilgilerle kayıtlı firma seçildi');
      pick(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.candidates) { setMatch(d.candidates); setDifferent(false); }
      toast.error(d?.message || (typeof d === 'string' ? d : 'Firma kaydedilemedi'));
    } finally {
      setBusy(false);
    }
  };
  const confirm = () => {
    const v = text.trim();
    if (v.length < 2) {
      toast.error('Firma adını girin');
      flashElement(rootRef.current?.closest('[data-guide-required="true"]') || rootRef.current);
      rootRef.current?.querySelector('input')?.focus();
      return;
    }
    const near = firms.filter(f => similar(norm(f.name), norm(v)) && norm(f.name) !== norm(v));
    if (near.length && !suggest && !different && !firms.some(f => norm(f.name) === norm(v))) { setSuggest(near); return; }
    create(v);
  };

  if (behalfId) return (
    <div className="flex items-center justify-between rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm" data-testid={`${testPrefix}-selected`}>
      <span className="flex items-center gap-2 font-medium text-sky-800"><Building2 className="h-4 w-4" />{name}{suffix}</span>
      <button type="button" onClick={() => { setText(name); pick(null); }} className="text-xs text-sky-700 hover:underline" data-testid={`${testPrefix}-change`}>Değiştir</button>
    </div>
  );
  return (
    <div ref={rootRef} className="space-y-2 rounded-md" data-testid={`${testPrefix}-picker`}>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => { setText(e.target.value); reset(); }} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), confirm())}
          placeholder={placeholder} data-testid={`${testPrefix}-input`} />
        <Button type="button" onClick={confirm} disabled={busy} data-testid={`${testPrefix}-confirm`}>{different ? 'Yeni firma olarak kaydet' : 'Devam'}</Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input value={tax} onChange={(e) => setTax(e.target.value.replace(/[^\d\s]/g, '').slice(0, 14))} inputMode="numeric"
          placeholder="Vergi No / TCKN (isteğe bağlı)" className={different ? 'border-amber-400' : ''} data-testid={`${testPrefix}-tax-input`} />
        <Input value={branch} onChange={(e) => setBranch(e.target.value.slice(0, 80))}
          placeholder="Şube adı / kodu (isteğe bağlı)" className={different ? 'border-amber-400' : ''} data-testid={`${testPrefix}-branch-input`} />
      </div>
      {match ? (
        <BehalfMatchPanel candidates={match} differentMode={different} onPick={pick} onDifferent={() => setDifferent(true)} onCancel={reset} testPrefix={testPrefix} />
      ) : suggest ? (
        <div className="space-y-2 rounded-md border-2 border-amber-300 bg-amber-50 p-3 text-sm" data-testid={`${testPrefix}-similar`}>
          <p className="font-medium text-amber-900">Benzer adla daha önce talep oluşturdunuz. Bunu mu demek istediniz?</p>
          <div className="flex flex-wrap gap-2">
            {suggest.map(f => <Button key={f.id} type="button" size="sm" variant="outline" className="bg-white" onClick={() => pick(f)} data-testid={`${testPrefix}-suggest-${f.id}`}>{firmLabel(f)}</Button>)}
          </div>
          <button type="button" onClick={() => create(text.trim())} className="text-xs text-amber-800 underline" data-testid={`${testPrefix}-create-new`}>Hayır, "{text.trim()}" yeni firma olarak eklensin</button>
        </div>
      ) : firms.length > 0 && (
        <div data-testid={`${testPrefix}-list`}>
          <p className="mb-1 text-xs text-slate-500">Daha önce kaydettiğiniz firmalar (aynı firmayı tekrar oluşturmamak için listeden seçin):</p>
          <div className="flex flex-wrap gap-1.5">
            {matches.map(f => (
              <button key={f.id} type="button" onClick={() => pick(f)} className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-white px-2.5 py-1 text-xs text-sky-800 hover:bg-sky-50" data-testid={`${testPrefix}-option-${f.id}`}>
                <Building2 className="h-3 w-3" />{firmLabel(f)}{f.tax_key ? ` · VKN ${f.tax_key}` : ''}
              </button>
            ))}
            {!matches.length && <span className="text-xs text-slate-400">Eşleşen firma yok; "Devam" ile yeni firma eklenir.</span>}
          </div>
        </div>
      )}
      <p className="flex items-center gap-1 text-[11px] text-slate-500"><Info className="h-3 w-3" />Firma belgeleri ve personel bu firmaya özel tutulur; ana firmanızın belgeleri taşınmaz.</p>
    </div>
  );
};
